import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { providersAPI, reviewsAPI } from '../api';
import ServiceCard from '../components/ServiceCard';
import StarRating from '../components/StarRating';
import LoadingSpinner from '../components/LoadingSpinner';

const ProviderProfilePage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      providersAPI.getById(id),
      reviewsAPI.getByProvider(id),
    ]).then(([provRes, revRes]) => {
      setData(provRes.data);
      setReviews(revRes.data.reviews || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingSpinner />;
  if (!data) return <div className="text-center py-12">Provider not found</div>;

  const { provider, profile, services, reviewStats } = data;

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-700 to-primary-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold">
              {provider.first_name?.[0]}{provider.last_name?.[0]}
            </div>
            <div>
              <h1 className="text-3xl font-bold">{profile?.business_name || `${provider.first_name} ${provider.last_name}`}</h1>
              <div className="flex items-center gap-4 mt-2 text-primary-200">
                {provider.location && <span>📍 {provider.location}</span>}
                <span className="flex items-center"><span className="text-yellow-400 mr-1">★</span> {Number(provider.avg_rating || 0).toFixed(1)} ({provider.total_reviews} reviews)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* About */}
            {profile?.service_description && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-semibold mb-3">About</h2>
                <p className="text-gray-600">{profile.service_description}</p>
              </div>
            )}

            {/* Services */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Services ({services?.length || 0})</h2>
              {services && services.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {services.map((s) => <ServiceCard key={s.id} service={s} />)}
                </div>
              ) : (
                <p className="text-gray-500">No services listed yet.</p>
              )}
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Reviews ({reviews.length})</h2>
              </div>
              {reviews.length === 0 ? (
                <p className="text-gray-500">No reviews yet.</p>
              ) : (
                <div className="max-h-[480px] overflow-y-auto pr-2 space-y-4 scrollbar-thin">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b pb-4 last:border-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm">{review.first_name} {review.last_name}</span>
                        <StarRating rating={review.rating} readonly size="sm" />
                      </div>
                      {review.review_text && <p className="text-gray-600 text-sm">{review.review_text}</p>}
                      <span className="text-xs text-gray-400 mt-1 block">{new Date(review.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold mb-3">Rating Summary</h3>
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-primary-600">{Number(reviewStats?.avg_rating || 0).toFixed(1)}</div>
                <StarRating rating={Math.round(reviewStats?.avg_rating || 0)} readonly size="lg" />
                <p className="text-gray-500 text-sm mt-1">{reviewStats?.total_reviews || 0} reviews</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold mb-3">Contact</h3>
              <Link to={`/messages?user=${provider.id}`}
                className="block w-full bg-primary-600 text-white text-center py-2.5 rounded-lg font-medium hover:bg-primary-700 transition">
                Send Message
              </Link>
            </div>

            {profile?.availability_schedule && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-semibold mb-3">Availability</h3>
                <p className="text-gray-600 text-sm">Contact provider for availability details.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderProfilePage;
